import moment from 'moment';
import {
    appSignOffMethodKey,
    emailSignOffMethodKey,
    onScreenSignOffMethodKey,
    physicalSignOffMethodKey
} from 'pages/employee-reviews/shared';

// Frontend copy for the employee sign-off method rows, keyed by the API's method key.
export const signOffMethodDescriptions = {
    [physicalSignOffMethodKey]: "Print and sign a PDF copy",
    [onScreenSignOffMethodKey]: "Employee enters their signature on screen during the review",
    [emailSignOffMethodKey]: "Email a temporary link to the driver for electronic sign-off",
    [appSignOffMethodKey]: "Employee can view and sign off the review via the app"
};

export const scheduleRestartText = "When you save, every employee this review type applies to will become due on the new start date and then repeat at the frequency you have set, regardless of when they were last reviewed.";

export const maxSignOffAlertDelayValue = 1000;

// The sign-off alert wait reuses the advance notice units. That lookup has no key column to
// resolve against, so days is referenced by id: 1 is days in lck.Review_Advance_Notice_Unit,
// which is also the default the Employee microservice stores when no unit is supplied.
export const defaultSignOffAlertDelayUnitId = 1;

// A completed review is only ever left awaiting the employee's signature when the
// signature was handed to them, and the Email and App methods are the only ones that do
// that - a physical or on-screen signature is taken at the review itself.
export function hasEmployeeDrivenSignOff(reviewType, reviewOptions) {
    const employeeDrivenMethodIds = (reviewOptions?.signOffMethods ?? [])
        .filter((method) => method.key === emailSignOffMethodKey || method.key === appSignOffMethodKey)
        .map((method) => method.id);

    return (reviewType?.signOffMethodIds ?? []).some((id) => employeeDrivenMethodIds.includes(id));
}

function emptyValueToNull(value) {
    if (value === "" || value === undefined) {
        return null;
    }
    return value;
}

// The five outcome levels are a fixed set: only the names are editable, so the
// editor model is seeded from the option defaults (colours stay on the options).
export function buildDefaultReviewType(reviewOptions) {
    return {
        name: "",
        enabled: true,
        schedule: {
            active: true,
            startDate: null,
            frequency: null,
            frequencyUnitId: null,
            advanceNoticeValue: null,
            advanceNoticeUnitId: null
        },
        filterByLocations: false,
        requireManagerSignature: false,
        outcomeLevelsActive: true,
        outcomeLevelsMandatory: false,
        allowCustomNextReviewDate: false,
        raiseAlertWhenPending: false,
        raiseAlertForSignOff: false,
        signOffAlertDelayValue: 0,
        signOffAlertDelayUnitId: defaultSignOffAlertDelayUnitId,
        employeeTypes: [],
        employeeCategories: [],
        employeeStatuses: [],
        employeeLocations: [],
        signOffMethodIds: [],
        topicIds: [],
        levels: (reviewOptions?.outcomeLevels ?? []).map((level) => {
            return {
                levelNumber: level.number,
                levelName: level.name
            };
        })
    };
}

export function mapIdsToOptions(ids, options) {
    if (ids == null || options == null) {
        return [];
    }

    return ids
        .map((id) => options.find((option) => option.id === id))
        .filter((option) => option != null);
}

// The API model carries id arrays for the applies-to pickers, but the editor works
// with the full option objects so the multi-selects can render names.
export function mapReviewTypeFromApi(apiReviewType, employeeOptions) {
    // An inactive review type is never scheduled, so its schedule is shown and saved as off.
    let scheduleActive = apiReviewType.schedule?.active;

    if (apiReviewType.enabled !== true) {
        scheduleActive = false;
    }

    return {
        ...apiReviewType,
        schedule: {
            ...apiReviewType.schedule,
            active: scheduleActive
        },
        employeeTypes: mapIdsToOptions(apiReviewType.employeeTypes, employeeOptions.employeeTypes),
        employeeCategories: mapIdsToOptions(apiReviewType.employeeCategories, employeeOptions.employeeCategories),
        employeeStatuses: mapIdsToOptions(apiReviewType.employeeStatuses, employeeOptions.employeeStatuses),
        employeeLocations: apiReviewType.employeeLocations ?? [],
        signOffMethodIds: apiReviewType.signOffMethodIds ?? [],
        topicIds: apiReviewType.topicIds ?? [],
        levels: apiReviewType.levels ?? []
    };
}

export function buildApiModel(reviewType, reviewOptions) {
    // The API rejects the sign-off alert unless a sign-off method hands the signature to
    // the employee, so the value cannot be kept against a review type with no such method
    // selected.
    const raiseAlertForSignOff = reviewType.raiseAlertForSignOff === true && hasEmployeeDrivenSignOff(reviewType, reviewOptions);

    // Both settings act on pending reviews, which only a schedulable review type has, so
    // neither is saved against one that is switched off or not scheduled.
    const schedulable = isSchedulable(reviewType);
    const allowCustomNextReviewDate = schedulable && reviewType.allowCustomNextReviewDate === true;
    const raiseAlertWhenPending = schedulable && reviewType.raiseAlertWhenPending === true;

    return {
        name: reviewType.name,
        enabled: reviewType.enabled,
        schedule: {
            active: reviewType.schedule.active,
            startDate: emptyValueToNull(reviewType.schedule.startDate),
            frequency: emptyValueToNull(reviewType.schedule.frequency),
            frequencyUnitId: emptyValueToNull(reviewType.schedule.frequencyUnitId),
            advanceNoticeValue: emptyValueToNull(reviewType.schedule.advanceNoticeValue),
            advanceNoticeUnitId: emptyValueToNull(reviewType.schedule.advanceNoticeUnitId)
        },
        filterByLocations: reviewType.filterByLocations,
        requireManagerSignature: reviewType.requireManagerSignature,
        outcomeLevelsActive: reviewType.outcomeLevelsActive,
        outcomeLevelsMandatory: reviewType.outcomeLevelsMandatory,
        allowCustomNextReviewDate: allowCustomNextReviewDate,
        raiseAlertWhenPending: raiseAlertWhenPending,
        raiseAlertForSignOff: raiseAlertForSignOff,
        signOffAlertDelayValue: raiseAlertForSignOff ? reviewType.signOffAlertDelayValue : 0,
        signOffAlertDelayUnitId: raiseAlertForSignOff ? reviewType.signOffAlertDelayUnitId : defaultSignOffAlertDelayUnitId,
        employeeTypes: reviewType.employeeTypes.map((item) => item.id),
        employeeCategories: reviewType.employeeCategories.map((item) => item.id),
        employeeStatuses: reviewType.employeeStatuses.map((item) => item.id),
        employeeLocations: reviewType.filterByLocations ? reviewType.employeeLocations : [],
        signOffMethodIds: reviewType.signOffMethodIds,
        topicIds: reviewType.topicIds,
        levels: reviewType.levels.map((level) => {
            return {
                levelNumber: level.levelNumber,
                levelName: level.levelName
            };
        })
    };
}

// Approximate day lengths used to compare schedule durations client-side. The unit
// lookups only expose display names, so units are matched on their singular base name.
const unitDayLengths = { day: 1, week: 7, month: 30.4, year: 365 };

function getUnitDayLength(units, unitId) {
    const unit = (units ?? []).find((item) => item.id === unitId);

    if (unit?.name == null) {
        return null;
    }

    let baseName = unit.name.toLowerCase();

    if (baseName.endsWith("s")) {
        baseName = baseName.substring(0, baseName.length - 1);
    }

    return unitDayLengths[baseName] ?? null;
}

// A review type is only scheduled while it is both switched on and set to schedule -
// either switch off leaves it raised on demand.
export function isSchedulable(reviewType) {
    return reviewType?.enabled === true && reviewType?.schedule?.active === true;
}

// Mirrors what a save keeps: both settings act on pending reviews, which only a
// schedulable review type has, so the editor clears them once such a type is saved.
export function clearUnschedulableSettings(reviewType) {
    if (isSchedulable(reviewType)) {
        return reviewType;
    }

    return {
        ...reviewType,
        raiseAlertWhenPending: false,
        allowCustomNextReviewDate: false
    };
}

export function isTodayOrLater(date) {
    return date != null && !moment(date).isBefore(moment().startOf('day'));
}

// A null originalReviewType means create mode - a review type set up as scheduled from
// new is held to the same today-or-later rule as one returning to scheduling.
export function startDateMustBeFuture(reviewType, originalReviewType) {
    return isSchedulable(reviewType) && (originalReviewType == null || !isSchedulable(originalReviewType));
}

// Returning to scheduling is a transition an already saved review type makes, so it
// never applies to one being created.
export function isReturningToScheduling(reviewType, originalReviewType) {
    return originalReviewType != null && startDateMustBeFuture(reviewType, originalReviewType);
}

// Only a saved scheduled review type has pending reviews for a save to remove, so leaving
// scheduling never applies to one being created.
export function isLeavingScheduling(reviewType, originalReviewType) {
    return isSchedulable(originalReviewType) && !isSchedulable(reviewType);
}

export function getDetailsValidationConfig(reviewType, reviewOptions, originalReviewType = null) {
    const rules = [
        { fieldName: "name", required: true, maxLength: 200 },
        { fieldName: "employeeTypes", required: true },
        { fieldName: "employeeCategories", required: true },
        { fieldName: "employeeStatuses", required: true }
    ];

    const externalRules = [];

    // The schedule fields only become required when the schedule is active.
    if (reviewType.schedule.active) {
        rules.push({ fieldName: "schedule.startDate", required: true });
        rules.push({ fieldName: "schedule.frequency", required: true });
        rules.push({ fieldName: "schedule.frequencyUnitId", required: true });
        rules.push({ fieldName: "schedule.advanceNoticeValue", required: true });
        rules.push({ fieldName: "schedule.advanceNoticeUnitId", required: true });

        // Scheduling from a date in the past would make every employee overdue the
        // moment the reviews are raised. An absent date is left to the required rule.
        if (startDateMustBeFuture(reviewType, originalReviewType) && reviewType.schedule.startDate != null &&
            !isTodayOrLater(reviewType.schedule.startDate)) {
            externalRules.push({
                fieldName: "schedule.startDate",
                valid: false,
                errorMessage: "The start date must be today or later."
            });
        }

        // A review appears in Pending Reviews ahead of its due date by the advance
        // notice, so the notice window must fit inside a single review cycle.
        const frequencyDays = getUnitDayLength(reviewOptions?.frequencyUnits, reviewType.schedule.frequencyUnitId);
        const advanceNoticeDays = getUnitDayLength(reviewOptions?.advanceNoticeUnits, reviewType.schedule.advanceNoticeUnitId);

        if (reviewType.schedule.frequency != null && reviewType.schedule.advanceNoticeValue != null &&
            frequencyDays != null && advanceNoticeDays != null &&
            (reviewType.schedule.advanceNoticeValue * advanceNoticeDays) >= (reviewType.schedule.frequency * frequencyDays)) {
            externalRules.push({
                fieldName: "schedule.advanceNotice",
                valid: false,
                errorMessage: "Advance notice must be shorter than how often the review occurs."
            });
        }
    }

    if (reviewType.filterByLocations && (reviewType.employeeLocations == null || reviewType.employeeLocations.length === 0)) {
        externalRules.push({
            fieldName: "employeeLocations",
            valid: false,
            errorMessage: "At least one location must be selected if filtering by location."
        });
    }

    return { rules, externalRules };
}

export function getTopicsValidationConfig(reviewType) {
    const externalRules = [];

    if (reviewType.topicIds.length === 0) {
        externalRules.push({
            fieldName: "topicIds",
            valid: false,
            errorMessage: "At least one topic must be selected."
        });
    }

    return { rules: [], externalRules };
}

export function getSignOffValidationConfig(reviewType, reviewOptions) {
    const rules = [];
    const externalRules = [];

    // The wait is only offered, and only means anything, while the sign-off alert is on
    // and a method leaves the signature with the employee.
    if (reviewType.raiseAlertForSignOff === true && hasEmployeeDrivenSignOff(reviewType, reviewOptions)) {
        rules.push({
            fieldName: "signOffAlertDelayValue",
            required: true,
            type: "number",
            minValue: 0,
            maxValue: maxSignOffAlertDelayValue
        });
        rules.push({
            fieldName: "signOffAlertDelayUnitId",
            required: true
        });
    }

    if (reviewType.signOffMethodIds.length === 0) {
        externalRules.push({
            fieldName: "signOffMethodIds",
            valid: false,
            errorMessage: "At least one sign-off method must be selected."
        });
    }

    const appSignOffMethodId = (reviewOptions?.signOffMethods ?? [])
        .find((method) => method.key === appSignOffMethodKey)?.id;

    if (appSignOffMethodId != null) {
        const hasAppSignOff = reviewType.signOffMethodIds.includes(appSignOffMethodId);
        const hasAlternative = reviewType.signOffMethodIds.some((id) => id !== appSignOffMethodId);

        if (hasAppSignOff && !hasAlternative) {
            externalRules.push({
                fieldName: "signOffMethodIds",
                valid: false,
                errorMessage: "App sign-off cannot be the only sign-off method selected."
            });
        }
    }

    if (reviewType.levels.some((level) => level.levelName == null || level.levelName.trim().length === 0)) {
        externalRules.push({
            fieldName: "levels",
            valid: false,
            errorMessage: "All levels must have a name."
        });
    }

    return { rules, externalRules };
}
